/**
 * NotificationBanner Component
 * Displays a banner requesting notification permission
 * 
 * Requirements: 17.1, 17.2, 17.3, 17.4, 17.5
 */

import { useState, useEffect } from 'react';
import { BellIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { useNotifications } from '@/hooks/useNotifications';

const BANNER_DISMISSED_KEY = 'notification-banner-dismissed';

export function NotificationBanner() {
  const { permission, requestPermission, isSupported } = useNotifications();
  const [isDismissed, setIsDismissed] = useState(() => {
    return localStorage.getItem(BANNER_DISMISSED_KEY) === 'true';
  });

  // Hide banner if not supported, permission already granted/denied, or dismissed
  const shouldShow = isSupported && permission === 'default' && !isDismissed;

  useEffect(() => {
    // Hide banner if permission changes to granted or denied
    if (permission !== 'default') {
      setIsDismissed(true);
      localStorage.setItem(BANNER_DISMISSED_KEY, 'true');
    }
  }, [permission]);

  const handleEnable = async () => {
    const result = await requestPermission();
    if (result === 'granted' || result === 'denied') {
      setIsDismissed(true);
      localStorage.setItem(BANNER_DISMISSED_KEY, 'true');
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem(BANNER_DISMISSED_KEY, 'true');
  };

  if (!shouldShow) {
    return null;
  }

  return (
    <div
      className="relative z-40 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800 safe-area-inset-top"
      role="banner"
      aria-label="Notification permission request"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <BellIcon className="w-6 h-6 text-primary flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                Enable notifications to stay updated
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">
                Get notified about priority tasks and important updates
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={handleEnable}
              className="whitespace-nowrap"
            >
              Enable
            </Button>
            <button
              onClick={handleDismiss}
              className="p-2 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-800/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              aria-label="Dismiss notification banner"
            >
              <XMarkIcon className="w-5 h-5 text-primary" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
